import React from "react";
import { observer } from "mobx-react";
import { Grid, Box, Alert } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import FormExtras from "../../../../common/components/formExtras";

import { EditionCreateStore } from "../../store";
import { runInAction } from "mobx";
import { InputString } from "../../../common/controls";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import { EditionPublicationLinkCustomField } from "../custom/EditionPublicationLinkCustomField";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { EditionTypeCreate } from "../../../editionType/components/create/EditionTypeCreate";
import { EditionTypeCreateStore } from "../../../editionType/store";
import { EditionContentType } from "../../../../api/graphql";
import { InputNumber } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { MusicalWorkCreate } from "../../../musicalWork/components/create/MusicalWorkCreate";
import { MusicalWorkCreateStore } from "../../../musicalWork/store";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputHtmlString } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { UserAttachmentEditionCreate } from "../../../userAttachmentEdition/components/create/UserAttachmentEditionCreate";
import { UserAttachmentEditionCreateStore } from "../../../userAttachmentEdition/store";
import { GridColumn } from "../../../common/types";

export type EditionCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  editionTypeSelectedValue?: { displayId: string; id: string } | null;
  onEditionTypeSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onEditionTypeChange?: (value: string) => void;
  editionTypeOptions?: { displayId: string; id: string }[];
  editionTypeError?: string;
  contentTypeError?: string;
  contentTypeOptions: { id: string; displayId: string }[];
  contentType: EditionContentType;
  publicationLink: string;
  publicationLinkError?: string;
  onPublicationLinkChange: (value: string) => void;
  onContentTypeChange?: (value: EditionContentType) => void;
  priceSelectedValue?: { displayId: string; id: string } | null;
  onPriceChange?: (value: number) => void;
  price: number;
  priceError?: string;
  worksSelectedValues: { displayId: string; id: string }[];
  onWorksSelected?: (value: { displayId: string; id: string }[]) => void;
  onWorksChange?: (value: string) => void;
  worksOptions?: { displayId: string; id: string }[];
  worksOptionsMap?: any;
  worksMiniFormMode?: "inline" | "tooltip";
  worksError?: string;
  viewFormWorks?: (id: string) => void;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  contractObservationSelectedValue?: { displayId: string; id: string } | null;
  onContractObservationChange?: (value: string) => void;
  contractObservation: string;
  contractObservationError?: string;
  userAttachmentsOnRestore: (id: string) => void;
  userAttachmentsOnCommitChanges: (changes: GridChangeSet) => void;
  userAttachmentsValidateChanges: (changes: GridChangeSet) => boolean;
  userAttachmentsRows: CrudRow[];
  userAttachmentEditionCreateStore: UserAttachmentEditionCreateStore;
  userAttachmentsError?: string;
  userAttachmentsVisibleColumns: GridColumn[];
  dummyCRUDRestoredObservable: number;
  editionCreateStore: EditionCreateStore;
};

export const EditionCreateForm: React.FC<EditionCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name = "",
    nameError,
    onNameChange = () => {},
    onEditionTypeSelected = () => {},
    editionTypeSelectedValue = null,
    editionTypeOptions = [],
    editionTypeError,
    onEditionTypeChange = () => {},
    contentTypeError,
    contentTypeOptions = [],
    contentType = "" as EditionContentType,
    publicationLink = "",
    publicationLinkError,
    onPublicationLinkChange = () => {},
    onContentTypeChange = () => {},
    price = 0,
    priceError,
    onPriceChange = () => {},
    worksOptionsMap,
    worksMiniFormMode,
    onWorksSelected = () => {},
    worksSelectedValues,
    worksOptions = [],
    worksError,
    onWorksChange = () => {},
    viewFormWorks = () => {},
    notes = "",
    notesError,
    onNotesChange = () => {},
    contractObservation = "",
    contractObservationError,
    onContractObservationChange = () => {},
    userAttachmentsOnRestore,
    userAttachmentsOnCommitChanges,
    userAttachmentsValidateChanges,
    userAttachmentsRows,
    userAttachmentEditionCreateStore,
    userAttachmentsError,
    userAttachmentsVisibleColumns,
    dummyCRUDRestoredObservable,
    editionCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (userAttachmentEditionCreateStore.status === "uninitialized") {
      userAttachmentEditionCreateStore.initialize();
    }

    return (
      <Grid>
        <FormRecordProvider
          values={{
            name,
            editionType: editionTypeSelectedValue,
            contentType,
            publicationLink,
            price,
            notes,
            contractObservation,
          }}
        >
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
            <FormExtras
              entity="Edition"
              mode="create"
              values={{
                name,
                editionType: editionTypeSelectedValue,
                contentType,
                publicationLink,
                price,
                notes,
                contractObservation,
              }}
              options={{ editionType: editionTypeOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "editionType":
                    onEditionTypeSelected?.(valor);
                    break;
                  case "contentType":
                    onContentTypeChange?.(valor);
                    break;
                  case "publicationLink":
                    onPublicationLinkChange?.(valor);
                    break;
                  case "price":
                    onPriceChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                  case "contractObservation":
                    onContractObservationChange?.(valor);
                    break;
                }
              }}
            />

          <GroupContainer
            id="editionWorkflow"
            errorText={editionCreateStore.cardState[
              "editionWorkflow"
            ].errors.join(", ")}
            title={langStore.groupEditionEditionWorkflowLabel}
            expanded={editionCreateStore.getCardState("editionWorkflow")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("editionWorkflow")
            }
          >
            <FormControl fullWidth>
              <Alert
                severity="info"
                variant="standard"
                sx={{
                  alignItems: "center",
                  "& .MuiAlert-message": {
                    fontSize: "1.05rem",
                    lineHeight: 1.55,
                    fontWeight: 500,
                  },
                  "& .MuiAlert-icon": {
                    fontSize: "2rem",
                    alignItems: "center",
                  },
                }}
              >
                {langStore.editionWorkflowCreateHint}
              </Alert>
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionData"
            errorText={editionCreateStore.getCardErrors("editionData")}
            title={langStore.groupEditionEditionDataLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("editionData")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("editionData")
            }
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name}
                error={Boolean(nameError)}
                warning={!nameError && Boolean(editionCreateStore.warnings["name"])}
                helperText={nameError || editionCreateStore.warnings["name"]?.message}
                label={langStore.EditionNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.EditionNameDescription}
              />
              <InputReference
                id="editionType"
                label={langStore.EditionEditionTypeLabel}
                error={Boolean(editionTypeError)}
                helperText={editionTypeError || editionCreateStore.warnings["editionType"]?.message}
                selectedValue={editionTypeSelectedValue}
                onChange={(value: string) => onEditionTypeChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onEditionTypeSelected(value)
                }
                options={editionTypeOptions}
                description={langStore.EditionEditionTypeDescription}
                canCreate={rootStore.authStore.hasPermissions("editionType_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new EditionTypeCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <EditionTypeCreate
                        editionTypeCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewEditionType"
                    cancelMethod="cancelNewEditionType"
                  />
                )}
              />
              <InputReference
                id="contentType"
                error={Boolean(contentTypeError)}
                helperText={contentTypeError || editionCreateStore.warnings["contentType"]?.message}
                label={langStore.EditionContentTypeLabel}
                options={contentTypeOptions}
                selectedValue={
                  contentType
                    ? {
                        id: contentType,
                        displayId:
                          contentTypeOptions.find(
                            (o: { id: string }) => o.id === contentType
                          )?.displayId || contentType,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onContentTypeChange(
                    (value?.id as EditionContentType) ||
                      ("" as EditionContentType)
                  )
                }
              />
              <EditionPublicationLinkCustomField
                value={publicationLink}
                error={Boolean(publicationLinkError)}
                helperText={publicationLinkError || editionCreateStore.warnings["publicationLink"]?.message}
                {...{ optional: true }}
                label={langStore.EditionPublicationLinkLabel}
                description={langStore.EditionPublicationLinkDescription}
                onChange={(value: string) => onPublicationLinkChange(value)}
              />
              <InputNumber
                value={price}
                error={Boolean(priceError)}
                allowNegative={true}
                warning={!priceError && Boolean(editionCreateStore.warnings["price"])}
                helperText={priceError || editionCreateStore.warnings["price"]?.message}
                label={langStore.EditionPriceLabel}
                decimalScale={2}
                prefix={"$"}
                thousandSeparator={","}
                fixedDecimalScale={true}
                description={langStore.EditionPriceDescription}
                onChange={(value: number) => onPriceChange(value)}
              />
              <InputReferenceSet
                id="works"
                label={langStore.EditionWorksLabel}
                error={Boolean(worksError)}
                helperText={worksError || editionCreateStore.warnings["works"]?.message}
                selectedValues={worksSelectedValues}
                onChange={(value: string) => onWorksChange(value)}
                onSelect={(value: { displayId: string; id: string }[]) =>
                  onWorksSelected(value)
                }
                options={worksOptions}
                optionsMap={worksOptionsMap}
                miniFormMode={worksMiniFormMode}
                description={langStore.EditionWorksDescription}
                onView={viewFormWorks}
                canCreate={rootStore.authStore.hasPermissions("musicalWork_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new MusicalWorkCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <MusicalWorkCreate
                        musicalWorkCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewMusicalWork"
                    cancelMethod="cancelNewMusicalWork"
                  />
                )}
              />

              <InputText
                value={notes}
                error={Boolean(notesError)}
                warning={!notesError && Boolean(editionCreateStore.warnings["notes"])}
                helperText={notesError || editionCreateStore.warnings["notes"]?.message}
                label={langStore.EditionNotesLabel}
                onChange={(value: string) => onNotesChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.EditionNotesDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="contracts"
            errorText={editionCreateStore.getCardErrors("contracts")}
            title={langStore.groupEditionContractsLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("contracts")}
            toggleExpand={() => editionCreateStore.toggleCardState("contracts")}
          >
            <FormControl fullWidth>
              <InputHtmlString
                value={contractObservation}
                error={Boolean(contractObservationError)}
                helperText={contractObservationError || editionCreateStore.warnings["contractObservation"]?.message}
                label={langStore.EditionContractObservationLabel}
                onChange={(value: string) => onContractObservationChange(value)}
                fragment={true}
                description={langStore.EditionContractObservationDescription}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionContracts"
            errorText={editionCreateStore.getCardErrors("editionContracts")}
            title={langStore.groupEditionEditionContractsLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("editionContracts")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("editionContracts")
            }
          >
            <FormControl fullWidth></FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionDocumentation"
            errorText={editionCreateStore.getCardErrors("editionDocumentation")}
            title={langStore.groupEditionEditionDocumentationLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("editionDocumentation")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("editionDocumentation")
            }
          >
            <FormControl fullWidth></FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionUserAttachments"
            errorText={editionCreateStore.getCardErrors(
              "editionUserAttachments"
            )}
            title={langStore.groupEditionEditionUserAttachmentsLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("editionUserAttachments")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("editionUserAttachments")
            }
          >
            <FormControl fullWidth>
              <ItemsCRUDCompact
                label={langStore.EditionUserAttachmentsLabel}
                description={langStore.EditionUserAttachmentsDescription}
                showLabel={false}
                rows={userAttachmentsRows}
                columns={userAttachmentsVisibleColumns}
                popupComponent={(row: any) => {
                  if (
                    row &&
                    row.id &&
                    !(typeof row.id === "string" && row.id.startsWith("auto-"))
                  ) {
                    runInAction(() =>
                      editionCreateStore.userAttachmentsLoadRow(row)
                    );
                  }
                  return (
                    <UserAttachmentEditionCreate
                      userAttachmentEditionCreateStore={
                        userAttachmentEditionCreateStore
                      }
                      showToolbox={false}
                      showSaveFooter={false}
                    />
                  );
                }}
                popupMaxWidth="sm"
                onRestore={(id: string) => userAttachmentsOnRestore(id)}
                onCommitChanges={(changes: GridChangeSet) =>
                  userAttachmentsOnCommitChanges(changes)
                }
                validate={(changes: GridChangeSet) =>
                  userAttachmentsValidateChanges(changes)
                }
                dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                error={userAttachmentsError}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="systemDates"
            errorText={editionCreateStore.getCardErrors("systemDates")}
            title={langStore.groupEditionSystemDatesLabel}
            collapsible={true}
            expanded={editionCreateStore.getCardState("systemDates")}
            toggleExpand={() =>
              editionCreateStore.toggleCardState("systemDates")
            }
          >
            <FormControl fullWidth></FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
        </FormRecordProvider>
      </Grid>
    );
  }
);
